'use client';

import NextLink from 'next/link';
import type { ComponentProps } from 'react';
import { useLocaleContext } from '@/context/locale-context';
import { withLocalePrefix } from '@/lib/locale';

/**
 * Länk som lägger på språkprefix åt dig.
 *
 * Använd ALLTID den här i stället för `next/link`. App Router prefixar INTE
 * länkar automatiskt — `locale`-propen på next/link gäller bara Pages Router och
 * ignoreras här (se node_modules/next/dist/client/app-dir/link.d.ts). Utan
 * wrappern tappar varje klick /en-prefixet och besökaren kastas ut ur den
 * engelska sajten. ESLint förbjuder direktimport av next/link utanför den här
 * filen.
 *
 * Prefixet läggs bara på när det ska: `withLocalePrefix` returnerar sökvägen
 * oförändrad för svenska besökare, för externa URL:er, ankare och mailto:, och
 * för sidtyper utanför den engelska omgången. En engelsk besökare som klickar
 * på /kassa hamnar alltså på den svenska kassan — avsiktligt, den är oöversatt.
 */
/**
 * next/link ärver en `locale`-prop som IGNORERAS i App Router — den gäller bara
 * Pages Router (node_modules/next/dist/client/app-dir/link.d.ts:131). Att den
 * inte gör något var hela skälet att bygga den här wrappern i stället för att
 * flytta sidträdet under [lang]. Den ska därför inte gå att skriva av misstag:
 * `locale?: never` gör den till ett typfel i stället för en tyst missuppfattning.
 */
type LocaleLinkProps = Omit<ComponentProps<typeof NextLink>, 'locale'> & { locale?: never };

export function Link({ href, ...rest }: LocaleLinkProps) {
  const { locale } = useLocaleContext();
  const target = typeof href === 'string' ? withLocalePrefix(href, locale) : href;
  return <NextLink href={target} {...rest} />;
}

export default Link;

/**
 * Språkmedveten sökväg för imperativ navigering.
 *
 *   const localePath = useLocalePath();
 *   router.push(localePath('/sok?q=broms'));
 *
 * Behövs eftersom `router.push` inte går genom <Link> och därför inte får
 * prefixet automatiskt. Funktionen är idempotent — en redan prefixad sökväg
 * lämnas orörd.
 */
export function useLocalePath(): (path: string) => string {
  const { locale } = useLocaleContext();
  return (path: string) => withLocalePrefix(path, locale);
}
